import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@sureco/design-system'
import { Alert, Input, Modal, Spin, Table, Typography, message } from 'antd'
import {
  FlexspringSourceFilePreviewDto,
  getFlexspringSourceFile,
} from '../api/termination-tracking-enrollment-platform-api-client'

const { Text } = Typography
const { Search } = Input

const PAGE_SIZE = 10

type Props = {
  open: boolean
  requestId?: string | null
  highlightEmployeeId?: string | null
  onClose: () => void
}

const isNotFound = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status
  return status === 404
}

const rowMatchesEmployee = (
  row: Record<string, string>,
  employeeId?: string | null
) => {
  if (!employeeId) return false
  const needle = employeeId.trim().toLowerCase()
  return Object.values(row).some(
    (value) => String(value).trim().toLowerCase() === needle
  )
}

const FlexspringSourceFileModal = ({
  open,
  requestId,
  highlightEmployeeId,
  onClose,
}: Props) => {
  const [file, setFile] = useState<FlexspringSourceFilePreviewDto | null>(null)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [locateEmployeeId, setLocateEmployeeId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!open) {
      setFile(null)
      setPage(1)
      setSearchInput('')
      setSearch('')
      setLocateEmployeeId(null)
      setErrorMessage('')
      return
    }
    setPage(1)
    setSearchInput('')
    setSearch('')
    setLocateEmployeeId(highlightEmployeeId ?? null)
  }, [open, requestId, highlightEmployeeId])

  useEffect(() => {
    if (!open || !requestId) {
      return
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setErrorMessage('')
      try {
        const data = await getFlexspringSourceFile(requestId, {
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          employeeId: search ? undefined : locateEmployeeId || undefined,
        })
        if (!cancelled) {
          setFile(data)
          if (locateEmployeeId) {
            setLocateEmployeeId(null)
            if (!search && data.page !== page) {
              setPage(data.page)
            }
          }
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setFile(null)
          setErrorMessage(
            isNotFound(err)
              ? 'FlexSpring file is still processing'
              : 'Failed to load source file rows.'
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [open, requestId, page, search, locateEmployeeId])

  const columns = useMemo(
    () =>
      (file?.columns ?? []).map((column) => ({
        title: column,
        dataIndex: column,
        key: column,
        ellipsis: true,
      })),
    [file?.columns]
  )

  const handleDownload = () => {
    if (!file?.downloadUrl) {
      message.error('Download is not available yet')
      return
    }
    const anchor = document.createElement('a')
    anchor.href = file.downloadUrl
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    anchor.click()
  }

  const handleSearch = (value: string) => {
    setSearch(value.trim())
    setPage(1)
    setLocateEmployeeId(null)
  }

  return (
    <Modal
      title={file?.fileName ? `Source: ${file.fileName}` : 'Source file'}
      open={open}
      onCancel={onClose}
      width={900}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="darkblue"
            onClick={handleDownload}
            disabled={!file?.downloadUrl || loading}
          >
            Download
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Search
          allowClear
          placeholder="Search source rows"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={handleSearch}
        />
        <Text type="secondary">
          {file?.rowCount != null
            ? search
              ? `${file.rowCount} matching rows in this FlexSpring file.`
              : `${file.rowCount} rows in this FlexSpring file.`
            : 'Showing rows in this FlexSpring file.'}
        </Text>
        {errorMessage ? (
          <Alert type="error" showIcon message={errorMessage} />
        ) : null}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 240,
            }}
          >
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="__rowKey"
            dataSource={(file?.rows ?? []).map((row, index) => ({
              ...row,
              __rowKey: `${page}-${index}`,
            }))}
            columns={columns}
            size="small"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: file?.rowCount ?? 0,
              hideOnSinglePage: true,
              onChange: (nextPage) => setPage(nextPage),
            }}
            scroll={{ x: true, y: 420 }}
            rowClassName={(row: Record<string, string>) =>
              rowMatchesEmployee(row, highlightEmployeeId)
                ? 'ant-table-row-selected'
                : ''
            }
            locale={{ emptyText: 'No source rows available' }}
          />
        )}
      </div>
    </Modal>
  )
}

export default FlexspringSourceFileModal
